/**
 * API Normalizers
 * Transform raw API responses to typed internal models.
 * @module api/normalizers
 */
export {
  normalizeModule as normalizeAdminModule,
  normalizeModulesList as normalizeAdminModulesList,
  normalizeDictItem as normalizeAdminDictItem,
  normalizeDict,
} from "./admin.normalizer"
export {
  normalizeModule as normalizeCatalogModule,
  normalizeModulesList as normalizeCatalogModulesList,
  normalizeModuleField as normalizeCatalogModuleField,
  normalizeModuleFields,
  normalizeModuleItem,
  normalizeModuleItems,
  normalizeDeleteResponse,
  normalizeToggleResponse,
  normalizeDuplicateResponse,
  normalizeExportResponse,
  normalizeFilledBoxes,
} from "./catalog.normalizer"
export * from "./orders.normalizer"
export * from "./auth.normalizer"
export * from "./pages.normalizer"
export * from "./gallery.normalizer"
export * from "./discogs.normalizer"
export * from "./custom.normalizer"
import { safeString, safeNumber, safeArray, safeObject } from "../safeHelpers"

/**
 * Normalize the orders-exclusion response from the reports endpoint.
 *
 * The endpoint may return the exclusion value directly as a string or inside
 * an object. Both forms are projected to the canonical client shape.
 *
 * @param {string|Object} data - Raw endpoint response data
 * @returns {{users: string}} Canonical users exclusion payload
 * @throws {TypeError} When the raw response has an unsupported shape
 */
export function normalizeReportsOrdersExclUsersResponse(data) {
  if (typeof data === "string") {
    return { users: data }
  }

  if (
    data !== null &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    typeof data.users === "string"
  ) {
    return { users: data.users }
  }

  throw new TypeError("Invalid reports orders exclusions response")
}

/**
 * Normalize user data from API response.
 * @param {Object} data - Raw API response data
 * @returns {Object} Normalized user object
 */
export function normalizeUser(data) {
  return {
    id: safeNumber(data?.id),
    login: safeString(data?.login),
    name: safeString(data?.name),
    email: safeString(data?.email),
    role: safeString(data?.role),
    active: data?.active === 1 || data?.active === true,
  }
}

/**
 * Normalize module data from API response.
 * @param {Object} data - Raw API response data
 * @returns {Object} Normalized module object
 */
export function normalizeModule(data) {
  return {
    id: safeNumber(data?.id),
    name: safeString(data?.name),
    title: safeString(data?.title),
    description: safeString(data?.description),
    active: data?.active === 1 || data?.active === true,
    fields: safeArray(data?.fields, safeObject),
  }
}

/**
 * Normalize module field from API response.
 * @param {Object} data - Raw API response data
 * @returns {Object} Normalized field object
 */
export function normalizeModuleField(data) {
  return {
    id: safeNumber(data?.id),
    name: safeString(data?.name),
    label: safeString(data?.label),
    type: safeString(data?.type),
    required: data?.required === 1 || data?.required === true,
    order: safeNumber(data?.order),
  }
}

/**
 * Normalize dictionary item from API response.
 * @param {Object} data - Raw API response data
 * @returns {Object} Normalized dictionary item
 */
export function normalizeDictItem(data) {
  return {
    id: safeNumber(data?.id),
    name: safeString(data?.name),
    value: data?.value,
    label: safeString(data?.label),
    active:
      data?.active === 1 || data?.active === true || data?.active === null,
  }
}

/**
 * Normalize auth token check response.
 * @param {Object} data - Raw API response data
 * @returns {Object} Normalized auth response
 */
export function normalizeAuthCheck(data) {
  return {
    isAuth: data?.isAuth === true || data?.isAuth === 1,
    user: data?.user ? normalizeUser(data.user) : null,
  }
}

/**
 * Normalize login response.
 * @param {Object} data - Raw API response data
 * @returns {Object} Normalized login response
 */
export function normalizeLogin(data) {
  return {
    success: data?.success === true || data?.success === 1,
    token: safeString(data?.token),
    user: data?.user ? normalizeUser(data.user) : null,
  }
}

/**
 * Normalize list response with items.
 * @param {Array} data - Raw API response array
 * @param {Function} normalizer - Function to normalize each item
 * @returns {Array} Normalized items array
 */
export function normalizeList(data, normalizer) {
  return safeArray(data, normalizer)
}
